import React, { MouseEvent, useContext, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, HStack, Td, Text, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import {
  DealMediaTypeEnum,
  MediaDocument,
  MediaQuery,
  MediaQueryVariables,
  PayoffRequestStatus,
  useUpdatePayoffClerkMutation,
} from '../../../../gql/generated/graphql';
import { PayoffRequest } from '../../../../gql/payoffRequestGql';

import Modal from '../../../shared/Modal';

import ClaimDealButton from './ClaimDealButton';
import ConfirmPayoff from './ConfirmPayoff';
import ConfirmPayoffFailure from './ConfirmPayoffFailure';
import RequestPayoffFailure from './RequestPayoffFailure';
import UploadPayoffDocs from './UploadPayoffDocs';

import { PermissionEnum } from '../../../../constants/permissions';
import { AbilityContext } from '../../../../libs/contextLib';

interface PayoffDealActionsProps {
  payoffRequest?: PayoffRequest;
  claimedBy?: string;
  dealId?: string;
}

const PayoffDealActions: React.FC<PayoffDealActionsProps> = ({
  payoffRequest,
  claimedBy,
  dealId,
}) => {
  const { user } = useAuth0();
  const ability = useContext(AbilityContext);

  const client = useApolloClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [checkingMedia, setCheckingMedia] = useState(false);
  const [update, { loading: updatingClaim }] = useUpdatePayoffClerkMutation();

  const showConfirmPayoffButton =
    payoffRequest && payoffRequest?.status !== PayoffRequestStatus.Completed;
  const canConfirmPayoffFailure = ability.has(PermissionEnum.ConfirmPayoffFailure);
  const isClaimedByCurrentUser = claimedBy === user?.sub;
  const allowedToClaimAndConfirm =
    ability.has(PermissionEnum.PayoffManager) || isClaimedByCurrentUser;

  const updateClaim = async () => {
    try {
      await update({
        variables: {
          payoff_request_id: payoffRequest?.id,
          payoff_clerk_id: claimedBy ? null : user?.sub,
        },
      });
      onClose();
    } catch {
      toast.error('Failed to update payoff claim. Please try again.');
    }
  };

  const onClaim = async (e: MouseEvent) => {
    e.stopPropagation();
    if (checkingMedia || updatingClaim || isOpen) {
      return;
    }

    const mediaDealId = dealId ? Number(dealId) : payoffRequest?.deal_id;
    if (!claimedBy && mediaDealId) {
      setCheckingMedia(true);
      try {
        const { data } = await client.query<MediaQuery, MediaQueryVariables>({
          query: MediaDocument,
          variables: { dealId: mediaDealId },
          fetchPolicy: 'network-only',
        });
        if (data.media?.some((media) => media?.type === DealMediaTypeEnum.PayoffDocs)) {
          onOpen();
          return;
        }
      } catch {
        toast.error('Unable to check payoff documents. Please try claiming again.');
        return;
      } finally {
        setCheckingMedia(false);
      }
    }

    await updateClaim();
  };
  return (
    <Td onClick={(e) => e.stopPropagation()}>
      <HStack spacing="0.5">
        <ClaimDealButton
          isClaimed={!!claimedBy}
          allowedToClaim={allowedToClaimAndConfirm}
          onClick={onClaim}
          isLoading={checkingMedia || updatingClaim}
        />
        {showConfirmPayoffButton ? (
          <ConfirmPayoff payoffRequest={payoffRequest} disabled={!allowedToClaimAndConfirm} />
        ) : (
          <UploadPayoffDocs payoffRequest={payoffRequest} disabled={!allowedToClaimAndConfirm} />
        )}
        {canConfirmPayoffFailure ? (
          <ConfirmPayoffFailure payoffRequest={payoffRequest} buttonType="button" />
        ) : (
          <RequestPayoffFailure
            payoffRequest={payoffRequest}
            disabled={
              !isClaimedByCurrentUser ||
              payoffRequest?.status === PayoffRequestStatus.FailureRequested
            }
          />
        )}
      </HStack>
      <Modal
        title="Payoff Document Uploaded"
        isOpen={isOpen}
        onClose={onClose}
        canDismiss={!updatingClaim}
        rightButtons={
          <HStack>
            <Button variant="secondary" onClick={onClose} isDisabled={updatingClaim}>
              CANCEL
            </Button>
            <Button onClick={updateClaim} isLoading={updatingClaim} isDisabled={!!claimedBy}>
              CLAIM
            </Button>
          </HStack>
        }
      >
        <Text>
          This deal has a payoff document in the media center. Please review it to validate their
          payoff
        </Text>
      </Modal>
    </Td>
  );
};

export default PayoffDealActions;
